import React                     from 'react';
import { isNullOrUndefined }     from 'rewire-common';
import {
  Observe,
  DataSignal,
  value,
}                                from 'rewire-core';
import { WithStyle }             from 'rewire-ui';
import CircularProgress          from '@material-ui/core/CircularProgress';
import Fade, {FadeProps}         from '@material-ui/core/Fade';
import { Theme, withStyles }     from '../utility/styles';

// patch fade to get rid of the error for passing the style.
const F = Fade as React.ComponentType<FadeProps & {style?: React.CSSProperties}>;

const styles = (theme: Theme) => ({
  root: {
    position:      'relative',
    display:       'flex',
    flexDirection: 'column',
    flex:          1,
    overflow:      'hidden',
  },
  wrapped: {
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    position:        'absolute',
    zIndex:          1,
    top:             0,
    left:            0,
    width:           '100%',
    height:          '100%',
    backgroundColor: `${theme.palette.dialogPrimary.main}10`
  },
  childrenContainer: {
    display:       'flex',
    flex:          '1',
    flexDirection: 'column',
    overflow:      'hidden',
  },
});

export type LoaderStyles = ReturnType<typeof styles>;

export interface ILoaderProps {
  loading?:             boolean;
  transitionDuration?:  number;
  minimumLoadDuration?: number;
  delay?:               number;
  showWhileLoading?:    boolean;
  classes?:             any;
  children:             JSX.Element;
}

export type LoaderProps = WithStyle<LoaderStyles, ILoaderProps>;

class Loader extends React.Component<LoaderProps> {
  _defaultMinimumLoadDuration: number;
  _defaultDelay:               number;
  _defaultTransitionDuration:  number;
  minimumTimeElapsed:          DataSignal<boolean>;
  minimumTimeElapsedTimeout:   any;

  constructor(props: LoaderProps) {
    super(props);

    this._defaultMinimumLoadDuration = 100;
    this._defaultDelay               = 0;
    this._defaultTransitionDuration  = 400;
    this.minimumTimeElapsed          = value(true);
  }

  setMinimumTimeElapsedTimeout() {
    const timeoutDuration = !isNullOrUndefined(this.props.minimumLoadDuration) ? this.props.minimumLoadDuration : this._defaultMinimumLoadDuration;
    if (timeoutDuration <= 0) {
      this.minimumTimeElapsed = value(true);
      return;
    }

    this.minimumTimeElapsed        = value(false);
    this.minimumTimeElapsedTimeout = setTimeout(() => {
      this.minimumTimeElapsed(true);
    }, timeoutDuration);
  }

  shouldComponentUpdate(nextProps: LoaderProps) {
    return nextProps.loading !== this.props.loading;
  }

  UNSAFE_componentWillUpdate() {
    if (this.props.loading) {
      this.setMinimumTimeElapsedTimeout();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.minimumTimeElapsedTimeout);
    this.minimumTimeElapsedTimeout = undefined;
  }

  render() {
    const { classes, loading, showWhileLoading, children } = this.props;
    const delay              = !isNullOrUndefined(this.props.delay) ? this.props.delay : this._defaultDelay;
    const transitionDuration = !isNullOrUndefined(this.props.transitionDuration) ? this.props.transitionDuration : this._defaultTransitionDuration;

    return (
      <div className={classes.root}>
        <Observe render = {() => (
          < >
            <F style={{transitionDelay: delay + 'ms'}} in={loading || !this.minimumTimeElapsed()} appear={loading || !this.minimumTimeElapsed()} timeout={{enter: 0, exit: transitionDuration * 0.75}} unmountOnExit>
              <div className={this.props.classes.wrapped}>
                <CircularProgress />
              </div>
            </F>
            {showWhileLoading || (!loading && this.minimumTimeElapsed())
             ? <F in={true} appear={true} timeout={{enter: transitionDuration, exit: 0}}>
                 <div className={classes.childrenContainer}>
                   {children}
                 </div>
               </F>
            : null}
          </>
        )} />
      </div>
    );
  }
}

export default withStyles<ILoaderProps>(styles, Loader);
